import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { FaBars, FaTimes } from "react-icons/fa";
import gradientBorder from "./GradientBorder";

/* =========================
   STYLED COMPONENTS
========================= */

const HeaderWrapper = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0px 5vw;
  width: 100%;
  max-width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(15px) saturate(180%);
  border-bottom: 1px solid rgba(120, 120, 120, 0.2);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
  transition: transform 0.3s ease;
  overflow-x: hidden;

  &.hidden {
    transform: translateY(-100%);
  }

  @media (max-width: 768px) {
    padding: 12px 4vw;
  }
`;

const Logo = styled.img`
  width: 100px;
  height: auto;
  cursor: pointer;
  transition: width 0.3s ease;

  @media (max-width: 768px) {
    width: 90px;
  }
`;

const NavLinks = styled.nav`
  @keyframes flow {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  display: flex;
  align-items: center;
  gap: 18px;

  a {
    font-family: Arial, sans-serif;
    font-size: 17px;
    color: #000;
    text-decoration: none;
    position: relative;
    cursor: pointer;
    transition: 0.3s ease;
    white-space: nowrap;

    &:hover,
    &:focus {
      background: linear-gradient(90deg, #7b3ef0, #c85ad0, #f9840a, #7b3ef0);
      background-size: 400%;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      animation: flow 8s ease-in-out infinite;
    }
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

export const ResumeButton = styled.a`
  padding: 10px 20px;
  border-radius: 50px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  transition: 0.3s ease;
  white-space: nowrap;

  ${gradientBorder}

  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 14px;
  }
`;

const HamburgerMenu = styled.button`
  display: none;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #000;
  padding: 6px;
  z-index: 1002;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const Overlay = styled.div`
  display: ${({ open }) => (open ? "block" : "none")};
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.45);
  z-index: 998;
  backdrop-filter: blur(4px);
`;

const MobileMenu = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  background: #fff;
  width: 100%;
  position: fixed;
  top: 72px;
  left: 0;
  z-index: 999;
  padding: 20px 16px 24px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
  transform: ${({ open }) => (open ? "translateY(0)" : "translateY(-120%)")};
  opacity: ${({ open }) => (open ? 1 : 0)};
  pointer-events: ${({ open }) => (open ? "auto" : "none")};
  transition: 0.3s ease;

  @media (min-width: 769px) {
    display: none;
  }

  a {
    width: 100%;
    max-width: 320px;
    text-align: center;
    color: #111;
    text-decoration: none;
    font-size: 16px;
    font-weight: 700;
    padding: 12px 16px;
    border-radius: 14px;
    cursor: pointer;
    transition: 0.25s ease;

    &:hover,
    &:focus {
      background: rgba(123, 62, 240, 0.08);
      color: #7b3ef0;
    }
  }

  ${ResumeButton} {
    margin-top: 8px;
    width: 100%;
    max-width: 320px;
    text-align: center;
    display: inline-flex;
    justify-content: center;
  }
`;

/* =========================
   COMPONENT
========================= */

const Header = ({
  ConsultancySectionRef,
  SectionRef,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  const toggleMenu = () => setIsOpen((prev) => !prev);
  const closeMenu = () => setIsOpen(false);

  // Close mobile menu on route change
  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen && window.innerWidth <= 768) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  // Hide header when scrolling down, show when scrolling up
  useEffect(() => {
    let lastY = window.scrollY;

    const onWindowScroll = () => {
      const currentY = window.scrollY;

      if (currentY > lastY && currentY > 80) {
        setIsHeaderVisible(false);
      } else {
        setIsHeaderVisible(true);
      }

      lastY = currentY;
    };

    window.addEventListener("scroll", onWindowScroll);
    return () => window.removeEventListener("scroll", onWindowScroll);
  }, []);

  const handleNavigate = (path) => {
    closeMenu();
    navigate(path);
  };

  const handleScrollToSection = (ref) => {
    closeMenu();

    if (!ref?.current) {
      navigate("/");
      return;
    }

    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    } else {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <HeaderWrapper className={!isHeaderVisible ? "hidden" : ""}>
        <Logo
          src="https://cdn.shopify.com/s/files/1/0851/4607/5431/files/PRAGAN_2.png?v=1763888959"
          alt="Pragan Tech Solutions Logo"
          onClick={() => handleNavigate("/")}
        />

        <NavLinks>
          <a onClick={() => handleNavigate("/")}>Home</a>
          <a onClick={() => handleScrollToSection(ConsultancySectionRef)}>
            Our Services
          </a>
          <a onClick={() => handleNavigate("/projects")}>Projects</a>
          <a onClick={() => handleScrollToSection(SectionRef)}>Goals</a>
          <a onClick={() => handleNavigate("/contact")}>ContactUs</a>
          <a onClick={() => handleNavigate("/aboutus")}>About</a>

          <ResumeButton
            href="/Brochure.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Brochure
          </ResumeButton>
        </NavLinks>

        <HamburgerMenu onClick={toggleMenu} aria-label="Toggle menu">
          {isOpen ? <FaTimes /> : <FaBars />}
        </HamburgerMenu>
      </HeaderWrapper>

      <Overlay open={isOpen} onClick={closeMenu} />

      <MobileMenu open={isOpen}>
        <a onClick={() => handleNavigate("/")}>Home</a>
        <a onClick={() => handleScrollToSection(ConsultancySectionRef)}>
          Our Services
        </a>
        <a onClick={() => handleNavigate("/projects")}>Projects</a>
        <a onClick={() => handleScrollToSection(SectionRef)}>Goals</a>
        <a onClick={() => handleNavigate("/contact")}>ContactUs</a>
        <a onClick={() => handleNavigate("/aboutus")}>About</a>

        <ResumeButton
          href="/Brochure.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          Brochure
        </ResumeButton>
      </MobileMenu>
    </>
  );
};

export default Header;